import { Button, ButtonGroup, ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import React from 'react';
import { DataRoomUser, DataRoom, DirectConnectMilestoneReport } from '../../shared/models';

interface DirectConnectParticipantActionButtonProps {
	isEmployee: boolean;
	user: DataRoomUser;
	dataRoom: DataRoom;
	milestone: DirectConnectMilestoneReport | null;
	optOutUser: (user: DataRoomUser) => void;
	sendEmail: (user: DataRoomUser) => void;
	progressDetails: (milestone: DirectConnectMilestoneReport | null) => void;
	contactDetails: (user: DataRoomUser) => void;
}

type DirectConnectParticipantActionItem = keyof typeof DirectConnectParticipantActionItemMap;
const DirectConnectParticipantActionItemMap = {
	ProgressDetails: 'Progress Details',
	ContactDetails: 'Contact Details',
	OptOut: 'Opt-Out',
	SendEmail: 'Send Email',
};

export const DirectConnectParticipantActionButton: React.FC<DirectConnectParticipantActionButtonProps> = ({
	isEmployee,
	user,
	dataRoom,
	milestone,
	optOutUser,
	sendEmail,
	progressDetails,
	contactDetails,
}) => {
	const [open, setOpen] = React.useState(false);
	const anchorRef = React.useRef<HTMLDivElement>(null);
	const isRoomOpen = dataRoom.roomStatus == 'Open';

	const handleToggle = () => {
		setOpen(prevOpen => !prevOpen);
	};

	const handleClick = (action: DirectConnectParticipantActionItem) => {
		switch (action) {
			case 'ProgressDetails':
				progressDetails(milestone);
				break;
			case 'ContactDetails':
				contactDetails(user);
				break;
			case 'OptOut':
				optOutUser(user);
				break;
			case 'SendEmail':
				sendEmail(user);
				break;
		}
	};

	const handleClose = (event: Event) => {
		if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
			return;
		}

		setOpen(false);
	};

	const handleMenuItemClick = (
		event: React.MouseEvent<HTMLLIElement, MouseEvent>,
		actionItem: DirectConnectParticipantActionItem
	) => {
		setOpen(false);
		handleClick(actionItem);
	};

	return (
		<>
			<ButtonGroup variant="contained" ref={anchorRef} aria-label="split button">
				<Button onClick={handleToggle}>Actions</Button>
				<Button
					size="small"
					aria-controls={open ? 'split-button-menu' : undefined}
					aria-expanded={open ? 'true' : undefined}
					aria-label="select merge strategy"
					aria-haspopup="menu"
					onClick={handleToggle}
				>
					<ArrowDropDownIcon />
				</Button>
			</ButtonGroup>
			<Popper
				open={open}
				style={{ zIndex: 1000, margin: 0 }}
				anchorEl={anchorRef.current}
				role={undefined}
				transition
				disablePortal
			>
				{({ TransitionProps, placement }) => (
					<Grow
						{...TransitionProps}
						style={{
							transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
						}}
					>
						<Paper>
							<ClickAwayListener onClickAway={handleClose}>
								<MenuList autoFocusItem>
									{milestone != null && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'ProgressDetails')}>
											{DirectConnectParticipantActionItemMap['ProgressDetails']}
										</MenuItem>
									)}
									{isEmployee && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'ContactDetails')}>
											{DirectConnectParticipantActionItemMap['ContactDetails']}
										</MenuItem>
									)}
									{isEmployee &&
										user.allowAccess &&
										user.dealBankStatus == 'active' &&
										!user.optedOut && (
											<MenuItem onClick={event => handleMenuItemClick(event, 'OptOut')}>
												{DirectConnectParticipantActionItemMap['OptOut']}
											</MenuItem>
										)}
									{isRoomOpen && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'SendEmail')}>
											{DirectConnectParticipantActionItemMap['SendEmail']}
										</MenuItem>
									)}
								</MenuList>
							</ClickAwayListener>
						</Paper>
					</Grow>
				)}
			</Popper>
		</>
	);
};
